import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import moment from 'moment';
import ContentLoader from '../loader/ContentLoader';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { suppliersService } from '../../services/suppliers.service';
import { adminSupplierDocumentService } from '../../services/admin-supplier-document.service';
import { currencySymbol } from '../../helper/commonHelper';
import {
  Badge, DETAIL_ACCENTS, EmptyState as Empty, HeroChip, IconBox, Pill, ProfileHero,
  detailLabelStyle, detailTableWrap, detailTdStyle, detailThStyle as thStyle, isEmpty, show, titleCase,
} from '../common/DetailUI';
import { ACTION_ICONS } from '../../config/siteConfig';
import { MarkupsSection, fileUrl } from '../common/AccountSections';
import { ProfileFields as Fields, ProfileSection as Section, profileTdStyle as tdStyle } from '../common/ProfileBlocks';

const MySwal = withReactContent(Swal);

/* ---------- styles ---------- */
const btnLight = { background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" };
const btnPrimary = { background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 };
const linkStyle = { color: "var(--btn-bg)", textDecoration: "none" };

// one accent per section so the page reads as colour-coded blocks
const ACCENTS = {
  agency: DETAIL_ACCENTS.indigo,
  contact: DETAIL_ACCENTS.sky,
  address: DETAIL_ACCENTS.teal,
  record: "#64748b",
};

const formatDate = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "-");
const formatDay = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const websiteHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const MOBILE_KEY = /^(mobile|mobile_?no|mobile_?number|phone|phone_?no|phone_?number|contact_?number)$/i;
const EMAIL_KEY = /^(email|email_?id|email_?address)$/i;
// first non-empty scalar whose key matches, searching nested objects up to 2 levels deep
const findField = (obj, keyRe, depth = 0) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return undefined;
  for (const [k, v] of Object.entries(obj)) {
    if (keyRe.test(k) && !isEmpty(v) && typeof v !== "object") return String(v);
  }
  if (depth >= 2) return undefined;
  for (const v of Object.values(obj)) {
    const found = findField(v, keyRe, depth + 1);
    if (!isEmpty(found)) return found;
  }
  return undefined;
};
const maskAccount = (n) => (isEmpty(n) ? "-" : String(n).length <= 4 ? n : "•••• " + String(n).slice(-4));
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const iconBtn = (color) => ({ background: color + "1a", color, border: "none", borderRadius: 8, height: 28, fontSize: 12, fontWeight: 600 });

/* ---------- Documents (list view with verify / reject) ---------- */
const DocumentsList = ({ documents, onVerify, accent = DETAIL_ACCENTS.violet }) => {
  const verified = documents.filter((d) => String(d.verification_status).toLowerCase() === "verified").length;
  return (
    <Section icon="bi-file-earmark-text-fill" title="Documents" subtitle={`${plural(documents.length, "document")} · ${verified} verified`} accent={accent}>
      {documents.length > 0 ? (
        <div className="table-responsive" style={detailTableWrap}>
          <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr>{["Type", "Name", "Preview", "Number", "Uploaded", "Status", "Verified On", "Remarks", "Actions"].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {documents.map((d) => {
                const url = fileUrl(d.doc_url);
                const status = String(d.verification_status).toLowerCase();
                return (
                  <tr key={d.id}>
                    <td style={tdStyle}>{titleCase(d.doc_type)}</td>
                    <td style={tdStyle}>{show(d.doc_name)}</td>
                    <td style={tdStyle}>
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" title="Open in new tab" className="d-inline-flex align-items-center justify-content-center"
                          style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#b91c1c", textDecoration: "none" }}>
                          {/\.pdf(\?|$)/i.test(url)
                            ? <i className="bi bi-file-earmark-pdf-fill" style={{ fontSize: 22 }}></i>
                            : <img src={url} alt={d.doc_name || "Document"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                        </a>
                      ) : "-"}
                    </td>
                    <td style={tdStyle}>{show(d.doc_number)}</td>
                    <td style={tdStyle}>{formatDay(d.created_at)}</td>
                    <td style={tdStyle}><Badge value={d.verification_status} /></td>
                    <td style={tdStyle}>{d.verified_at ? formatDay(d.verified_at) : "-"}</td>
                    <td style={{ ...tdStyle, whiteSpace: "normal", minWidth: 140 }}>{show(d.remarks)}</td>
                    <td style={tdStyle}>
                      <div className="d-flex align-items-center gap-2">
                        {/* once verified or rejected, the decision is final: show it instead of the buttons */}
                        {status === "verified" || status === "rejected" ? (
                          <span className="fw-semibold" style={{ fontSize: 12.5, color: status === "verified" ? "#15803d" : "#b91c1c" }}>
                            <i className={`bi ${status === "verified" ? "bi-check-circle-fill" : "bi-x-circle-fill"} me-1`}></i>
                            {status === "verified" ? "Verified" : "Rejected"}
                          </span>
                        ) : (
                          <>
                            <button type="button" className="btn btn-sm d-flex align-items-center gap-1" style={iconBtn("#15803d")} onClick={() => onVerify(d, "verified")}>
                              <i className="bi bi-check-circle"></i> Verify
                            </button>
                            <button type="button" className="btn btn-sm d-flex align-items-center gap-1" style={iconBtn("#b91c1c")} onClick={() => onVerify(d, "rejected")}>
                              <i className="bi bi-x-circle"></i> Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <Empty text="No documents uploaded" />}
    </Section>
  );
};

/* ---------- Bank details (list view; only BankDetails fields) ---------- */
const BankList = ({ banks, accent = DETAIL_ACCENTS.pink }) => {
  const [revealed, setRevealed] = useState({});
  return (
    <Section icon="bi-bank2" title="Bank Details" subtitle={plural(banks.length, "account")} accent={accent}>
      {banks.length > 0 ? (
        <div className="table-responsive" style={detailTableWrap}>
          <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr>{["Bank Name", "Account Holder", "Account Number", "Branch", "Type", "IFSC Code", "Primary", "Added On"].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {banks.map((b) => {
                const shown = !!revealed[b.id];
                return (
                  <tr key={b.id}>
                    <td style={tdStyle} className="fw-semibold">{show(b.bank_name)}</td>
                    <td style={tdStyle}>{show(b.account_holder_name)}</td>
                    <td style={tdStyle}>
                      <span className="d-inline-flex align-items-center gap-2">
                        <span style={{ letterSpacing: 0.5 }}>{shown ? b.account_number : maskAccount(b.account_number)}</span>
                        {!isEmpty(b.account_number) && (
                          <button type="button" title={shown ? "Hide" : "Show"} className="btn btn-sm p-0 border-0" style={{ color: accent, lineHeight: 1 }}
                            onClick={() => setRevealed((prev) => ({ ...prev, [b.id]: !prev[b.id] }))}>
                            <i className={"bi " + (shown ? "bi-eye-slash" : "bi-eye")}></i>
                          </button>
                        )}
                      </span>
                    </td>
                    <td style={tdStyle}>{show(b.branch_name)}</td>
                    <td style={tdStyle}>{titleCase(b.account_type)}</td>
                    <td style={tdStyle}>{show(b.ifsc_code)}</td>
                    <td style={tdStyle}>{b.is_primary ? <Pill value="Primary" accent={accent} /> : "-"}</td>
                    {/* <td style={tdStyle}><Badge value={b.status} /></td> */}
                    <td style={tdStyle}>{formatDay(b.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <Empty text="No bank accounts added" />}
    </Section>
  );
};

const SupplierDetails = () => {
  const { supplierCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);

  useEffect(() => {
    loadDetail();
  }, [supplierCode]);

  /* ---------- API: details by supplier code ---------- */
  const loadDetail = async () => {
    try {
      setLoading(true);
      const res = await suppliersService.detailsByCode(supplierCode);
      if (res?.success) {
        let data = res.resData;
        // the by-code endpoint can omit contact fields; fill the gaps from the by-id endpoint
        if (data?.id && (isEmpty(data.mobile) || isEmpty(data.email))) {
          const full = await suppliersService.details(data.id);
          const f = full?.resData?.supplier ?? full?.resData;
          if (full?.success && f) data = { ...f, ...Object.fromEntries(Object.entries(data).filter(([, v]) => !isEmpty(v))) };
        }
        // the contact fields may arrive under another key or nested object; look for them before giving up
        const mobile = isEmpty(data?.mobile) ? findField(data, MOBILE_KEY) : data.mobile;
        const email = isEmpty(data?.email) ? findField(data, EMAIL_KEY) : data.email;
        if (isEmpty(mobile) || isEmpty(email)) console.warn("Supplier detail has no mobile/email. Response keys:", data && Object.keys(data), data);
        setSupplier(data ? { ...data, mobile, email } : data);
      } else {
        setSupplier(null);
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  // verify / reject a document; a remark is required for both
  const handleVerifyDocument = async (doc, verification_status) => {
    const verify = verification_status === "verified";
    const accent = verify ? "#15803d" : "#b91c1c";
    const MAX_REMARK = 250;
    const docLabel = `${titleCase(doc.doc_type)}${doc.doc_name ? " · " + doc.doc_name : ""}`;
    const result = await MySwal.fire({
      title: verify ? "Verify document?" : "Reject document?",
      icon: verify ? "question" : "warning",
      html: (
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 14, textAlign: "center", wordBreak: "break-word" }}>{docLabel}</div>
          <label htmlFor="doc-remark" style={{ fontSize: 12.5, fontWeight: 600, color: "#334155", marginBottom: 6, display: "block" }}>
            Remark <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <textarea
            id="doc-remark"
            rows={4}
            maxLength={MAX_REMARK}
            placeholder={verify ? "Add a note about this verification" : "Tell the supplier why this document is rejected"}
            style={{
              width: "100%", resize: "none", padding: "10px 12px", fontSize: 13.5, color: "#0f172a",
              border: "1.5px solid #e2e8f0", borderRadius: 10, background: "#fbfcfe", outline: "none", boxSizing: "border-box",
            }}
            onFocus={(e) => {
              const c = e.target.dataset.invalid ? "#ef4444" : accent;
              e.target.style.borderColor = c; e.target.style.boxShadow = `0 0 0 3px ${c}26`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = e.target.dataset.invalid ? "#ef4444" : "#e2e8f0"; e.target.style.boxShadow = "none";
            }}
            onChange={(e) => {
              delete e.target.dataset.invalid;
              e.target.style.background = "#fbfcfe";
              e.target.style.borderColor = accent; e.target.style.boxShadow = `0 0 0 3px ${accent}26`;
              document.getElementById("doc-remark-count").textContent = `${e.target.value.length}/${MAX_REMARK}`;
            }}
          />
          <div id="doc-remark-count" style={{ fontSize: 11, color: "#94a3b8", textAlign: "right", marginTop: 4 }}>0/{MAX_REMARK}</div>
        </div>
      ),
      focusConfirm: false,
      didOpen: () => document.getElementById("doc-remark")?.focus(),
      preConfirm: () => {
        const box = document.getElementById("doc-remark");
        const remark = box?.value.trim();
        if (!remark) {
          // no message: just turn the box red and keep the popup open
          box.dataset.invalid = "1";
          box.style.borderColor = "#ef4444";
          box.style.background = "#fff5f5";
          box.style.boxShadow = "0 0 0 3px #ef444426";
          box.focus();
          return false;
        }
        return remark;
      },
      showCancelButton: true,
      confirmButtonText: verify ? "Yes, verify" : "Yes, reject",
      confirmButtonColor: accent,
    });
    if (!result.isConfirmed) return;
    const res = await adminSupplierDocumentService.verifyDocument(doc.id, {
      verification_status,
      remarks: result.value,
    });
    if (res?.success) {
      toast.success(res.message || `Document ${verification_status}`);
      loadDetail();
    } else {
      toast.error(res?.message || "Failed to update document");
    }
  };

  const contactName = supplier ? ([supplier.first_name, supplier.last_name].filter(Boolean).join(" ") || supplier.head_person) : "";
  const location = supplier ? [supplier.city?.name, supplier.state?.name, supplier.country?.name].filter(Boolean).join(", ") : "";
  const wallet = supplier?.wallet;
  const symbol = currencySymbol(wallet?.currency_code);
  const money = (v) => (isEmpty(v) ? "-" : `${symbol} ${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);

  return (
    <section className="section dashboard">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Supplier Details</h4>
          <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
            <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <Link to="/suppliers/list" style={{ color: "#475569" }}>Supplier List</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <span className="fw-semibold" style={{ color: "#4a5568" }}>{supplierCode}</span>
          </nav>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button type="button" onClick={loadDetail} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={btnLight}>
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
          {supplier && (
            <button type="button" onClick={() => navigate("/suppliers/add", { state: { supplierCode: supplier.supplier_code } })}
              className="btn btn-sm d-flex align-items-center gap-1 px-3" style={btnLight}>
              <i className="bi bi-pencil"></i> Edit
            </button>
          )}
          <Link to="/suppliers/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={btnPrimary}>
            <i className="bi bi-list-ul"></i> Supplier List
          </Link>
        </div>
      </div>

      {loading ? <ContentLoader loaderType="circleSpin" /> : !supplier ? (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #eef1f7", padding: 40, textAlign: "center" }}>
          <i className="bi bi-search" style={{ fontSize: 32, color: "#b4bdd0" }}></i>
          <div className="fw-semibold mt-2" style={{ color: "#4a5568" }}>Supplier not found</div>
          <div style={{ fontSize: 13, color: "#475569" }}>No supplier exists with code {supplierCode}.</div>
        </div>
      ) : (
        <>
          <ProfileHero
            logo={fileUrl(supplier.logo_url)}
            icon="bi-truck"
            title={supplier.agency_name}
            //right={<Badge value={supplier.status} />}
            // chips={
            //   <>
            //     <HeroChip icon="bi-hash">{supplier.supplier_code}</HeroChip>
            //     {supplier.custom_code && <HeroChip icon="bi-tag">{supplier.custom_code}</HeroChip>}
            //     {supplier.inventory_status && <HeroChip icon="bi-box-seam">Inventory {titleCase(supplier.inventory_status)}</HeroChip>}
            //   </>
            // }
            meta={[
              { icon: "bi-person", text: contactName },
              { icon: "bi-telephone", text: supplier.mobile, href: supplier.mobile ? `tel:${supplier.mobile}` : null },
              { icon: "bi-envelope", text: supplier.email, href: supplier.email ? `mailto:${supplier.email}` : null },
              //{ icon: "bi-geo-alt", text: location },
            ]}
          />

          <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-body px-3 px-md-4 pb-3 pb-md-4 pt-0">
              <Section icon="bi-building-fill" title="Agency" accent={ACCENTS.agency}>
                <Fields
                  items={[
                    { label: "Supplier Code", value: supplier.supplier_code },
                    { label: "Custom Code", value: supplier.custom_code },
                    { label: "Agency Name", value: supplier.agency_name },
                    { label: "Status", value: supplier.status, badge: true },
                    { label: "Inventory Status", value: supplier.inventory_status, badge: true },
                    {
                      label: "Website",
                      value: supplier.website_url && (
                        <a href={websiteHref(supplier.website_url)} target="_blank" rel="noreferrer" style={linkStyle}>{supplier.website_url}</a>
                      ),
                    },
                  ]}
                />
              </Section>

              <Section icon="bi-person-lines-fill" title="Contact Person" accent={ACCENTS.contact}>
                <Fields
                  items={[
                    { label: "Contact Person", value: contactName },
                    { label: "Gender", value: supplier.gender ? titleCase(supplier.gender) : null },
                    { label: "Mobile", value: supplier.mobile },
                    { label: "WhatsApp", value: supplier.whatsapp_number },
                    { label: "Email", value: supplier.email },
                  ]}
                />
              </Section>

              <Section icon="bi-geo-alt-fill" title="Address" accent={ACCENTS.address}>
                <Fields
                  items={[
                    { label: "Country", value: supplier.country?.name },
                    { label: "State", value: supplier.state?.name },
                    { label: "City", value: supplier.city?.name },
                    { label: "Pincode", value: supplier.pincode },
                    { label: "Address", value: supplier.address, full: true },
                  ]}
                />
              </Section>

              <Section icon="bi-wallet2" title="Wallet" accent={DETAIL_ACCENTS.green}>
                <Fields
                  items={[
                    { label: "Wallet Balance", value: money(wallet?.current_balance) },
                    { label: "Currency", value: wallet?.currency_code },                 
                    { label: "Wallet Status", value: wallet?.status, badge: true },                       
                    //{ label: "Hold Balance", value: money(wallet?.hold_balance) },
                  ]}
                />
              </Section>

              <DocumentsList documents={supplier.documents || []} onVerify={handleVerifyDocument} />
              <BankList banks={supplier.bank_details || []} />

              <Section icon="bi-clock-history" title="Record Information" accent={ACCENTS.record}>
                <Fields
                  items={[
                    { label: "Created At", value: isEmpty(supplier.created_at) ? null : formatDate(supplier.created_at) },
                    { label: "Last Modified", value: isEmpty(supplier.modified_at) ? null : formatDate(supplier.modified_at) },
                    { label: "IP Address", value: supplier.ip_address },
                  ]}
                />
              </Section>
            </div>
          </div>

          {Array.isArray(supplier.markups) && <div className="mt-3"><MarkupsSection markups={supplier.markups} /></div>}
        </>
      )}
    </section>
  )
}

export default SupplierDetails;
